import { useEffect, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import { ethers } from 'ethers';
import DJackTotalPointsScreen from './DJackGameTotalPointsScreen';
import DJackGameTotalPointsText from './DJackGameTotalPointsText';
import PlayerIcon from '../Utils/PlayerIcon';

interface IProps {
  displayIndex: number;
  players: string[];
  totalPoints: number[];
}

const shorter = (str: any) =>
  str?.length > 8 ? `${str.slice(0, 5)}...${str.slice(-4)}` : str;

const getMax = (arr: number[]) => Math.max(...arr.filter((item) => item < 14));

const DJackGameTotalPoints: React.FC<IProps> = ({
  displayIndex,
  players,
  totalPoints
}: IProps) => {
  // states and contexts
  const [winState, setWinState] = useState<string>(
    displayIndex === 1 ? 'win' : 'lose'
  );
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState<number>(0);
  const [currentPoint, setCurrentPoint] = useState<number>(0);
  const { account } = useWeb3React<Web3Provider>();

  // functions
  const calculateWinState = () => {
    const maxPoint = getMax(totalPoints);
    const maxIndex = totalPoints.indexOf(maxPoint);
    const secondMaxPoint = getMax(
      totalPoints.filter((_, index) => index !== maxIndex)
    );
    let secondMaxIndex = totalPoints
      .filter((_, index) => index !== maxIndex)
      .indexOf(secondMaxPoint);
    if (secondMaxIndex >= maxIndex) {
      secondMaxIndex += 1;
    }
    if (!account || !players.includes(account)) {
      if (displayIndex === 1) {
        setCurrentPlayerIndex(maxIndex);
        setCurrentPoint(maxPoint);
        if (maxPoint === secondMaxPoint) {
          setWinState('draw');
        } else {
          setWinState('win');
        }
      } else {
        setCurrentPlayerIndex(secondMaxIndex);
        setCurrentPoint(secondMaxPoint);
        if (maxPoint === secondMaxPoint) {
          setWinState('draw');
        } else {
          setWinState('lose');
        }
      }
    } else {
      const accountIndex = players.indexOf(account);
      const accountPoint = totalPoints[accountIndex];
      if (displayIndex === 1) {
        setCurrentPlayerIndex(accountIndex);
        setCurrentPoint(accountPoint);
        if (maxPoint === secondMaxPoint && accountPoint === maxPoint) {
          setWinState('draw');
        } else if (accountPoint === maxPoint) {
          setWinState('win');
        } else {
          setWinState('lose');
        }
      } else if (maxPoint === secondMaxPoint && accountPoint === maxPoint) {
        setCurrentPlayerIndex(
          maxIndex !== accountIndex ? maxIndex : secondMaxIndex
        );
        setCurrentPoint(maxPoint);
        setWinState('draw');
      } else if (accountPoint === maxPoint) {
        setCurrentPlayerIndex(secondMaxIndex);
        setCurrentPoint(secondMaxPoint);
        setWinState('lose');
      } else {
        setCurrentPlayerIndex(maxIndex);
        setCurrentPoint(maxPoint);
        setWinState('win');
      }
    }
  };

  useEffect(() => {
    calculateWinState();
  }, [players, totalPoints]);

  return (
    <div className="djackGameTotalPointsWrapper">
      {displayIndex === 2 && (
        <div className="djackGameTotalPointsPlayerContainer">
          <div className="djackGameTotalPointsTextContainer">
            <span className="djackGameTotalPointsHeader">
              {!account || !players.includes(account) ? 'SECOND' : 'HIGHEST'}
            </span>
            <DJackGameTotalPointsText totalPointsInput={currentPoint} />
          </div>
          <div className="playerWrapper">
            <div className="playerIcon">
              <PlayerIcon active={false} />
            </div>
            <div className="playerText" style={{ color: '#d3d3d3' }}>
              {players[currentPlayerIndex] !== ethers.constants.AddressZero
                ? shorter(players[currentPlayerIndex])
                : '-'}
            </div>
          </div>
        </div>
      )}
      <DJackTotalPointsScreen
        totalPointsInput={currentPoint}
        winState={winState}
      />
      {displayIndex === 1 && (
        <div className="djackGameTotalPointsPlayerContainer">
          <div className="djackGameTotalPointsTextContainer">
            <span className="djackGameTotalPointsHeader">
              {!account || !players.includes(account) ? 'HIGHEST' : 'PLAYER'}
            </span>
            <DJackGameTotalPointsText totalPointsInput={currentPoint} />
          </div>
          <div className="playerWrapper">
            <div className="playerIcon">
              <PlayerIcon active={!!account} />
            </div>
            <div
              className="playerText"
              style={{ color: account ? '#f8f8f8' : '#d3d3d3' }}
            >
              {players[currentPlayerIndex] !== ethers.constants.AddressZero
                ? shorter(players[currentPlayerIndex])
                : '-'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DJackGameTotalPoints;
